import axios, { AxiosError } from "axios";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Backend tüm uçlardan şu zarfı döndürür:
 *   { isSuccess, message, statusCode, data, pagination?, statistics?, action? }
 *
 * Frontend kodu zarftan habersiz çalışsın diye response interceptor'ı `data`'yı
 * dışarı çıkarır; pagination varsa eski `PagedResult<T>` shape'ine çevirir.
 */
export interface ApiEnvelope<T> {
  isSuccess: boolean;
  message: string;
  statusCode: number;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
  statistics?: unknown;
  action?: unknown;
}

export interface ApiErrorPayload {
  isSuccess: false;
  statusCode: number;
  message: string;
  data?: Record<string, string[]> | null;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    const env = response.data as ApiEnvelope<unknown> | undefined;
    if (!env || typeof env !== "object" || !("isSuccess" in env)) {
      return response;
    }

    if (env.pagination) {
      response.data = {
        items: env.data ?? [],
        totalCount: env.pagination.total,
        page: env.pagination.page,
        pageSize: env.pagination.limit,
        totalPages: env.pagination.pages,
      };
    } else {
      response.data = env.data;
    }
    return response;
  },
  (error: AxiosError<ApiErrorPayload>) => {
    const status = error.response?.status;
    const payload = error.response?.data;

    if (status === 401) {
      useAuthStore.getState().logout();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    const message = payload?.message ?? error.message ?? "Bir hata oluştu.";

    // Backend paket kotasını 402 (Payment Required) ile döner. Kullanıcıyı
    // fiyatlandırma sayfasına yönlendiren aksiyonlu bir toast gösteririz.
    if (status === 402) {
      toast.error(message, {
        duration: 8000,
        action: {
          label: "Paketi yükselt",
          onClick: () => {
            window.location.href = "/fiyatlandirma";
          },
        },
      });
      return Promise.reject(error);
    }

    toast.error(message);
    return Promise.reject(error);
  }
);
